/**
 * Leaves API + TanStack Query hooks (Prompt 11E.6).
 *
 * Mirrors the pattern in ./attendance.ts and
 * @features/parent-portal/api/requests.ts (apply → pending → admin
 * approve/deny, note-carrying decisions).
 *
 * Endpoints:
 *   POST /v1/my/leaves                          — self-apply
 *   GET  /v1/my/leaves                           — own history
 *   GET  /v1/my/leaves/balance                   — own balance
 *   GET  /v1/staff/{staff_id}/leaves             — admin: one staff member
 *   GET  /v1/staff/{staff_id}/leaves/balance      — admin: one staff member
 *   POST /v1/staff/{staff_id}/leaves/manual       — admin: backdated entry
 *   GET  /v1/leaves                               — admin approval queue
 *   POST /v1/leaves/{id}/approve
 *   POST /v1/leaves/{id}/deny
 *   GET  /v1/config/leaves                        — leave types + allocation
 *   PATCH /v1/config/leaves                       — owner/admin: set allocation
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';

// Hand-declared rather than sourced from `components['schemas']` in
// @api/index — shared/api-types/index.ts is regenerated from the backend's
// live OpenAPI dump and is currently stale for several unrelated endpoints
// (see people/api/staff.ts's "not yet regenerated" comment for the existing
// precedent); regenerating it surfaced pre-existing drift on the Staff/
// Student/Package/Subject list endpoints unrelated to 11E.6, so these types
// are declared by hand here instead, matching the backend's Pydantic
// schemas in backend/app/features/leaves/schemas.py exactly.
export type LeaveType = 'casual' | 'sick' | 'other';
export type LeaveStatus = 'pending' | 'approved' | 'denied';

export interface LeaveResponse {
  id: string;
  staff_id: string;
  leave_type: LeaveType;
  status: LeaveStatus;
  start_date: string;
  end_date: string;
  days: number;
  note: string | null;
  applied_by_uid: string;
  is_backdated_entry: boolean;
  decided_by_uid: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeaveListResponse {
  items: LeaveResponse[];
  total: number;
}

export interface LeaveBalanceResponse {
  staff_id: string;
  year: number;
  allocated: number;
  availed: number;
  balance: number;
}

export interface LeaveApplyRequest {
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  note?: string;
}

export type LeaveManualEntryRequest = LeaveApplyRequest;

export interface LeaveDecision {
  note?: string;
}

export interface LeavesConfig {
  leave_types: string[];
  annual_leave_allocation: number;
  annual_leave_allocation_min: number;
  annual_leave_allocation_max: number;
  note_max_len: number;
  max_leave_span_days: number;
  flags: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const leavesKeys = {
  all: ['leaves'] as const,
  my: () => ['leaves', 'my'] as const,
  myBalance: (year?: number) => ['leaves', 'my', 'balance', year ?? 'current'] as const,
  staff: (staffId: string) => ['leaves', 'staff', staffId] as const,
  staffBalance: (staffId: string, year?: number) =>
    ['leaves', 'staff', staffId, 'balance', year ?? 'current'] as const,
  queue: (status?: string) => ['leaves', 'queue', status ?? 'all'] as const,
  config: () => ['config', 'leaves'] as const,
};

// ---------------------------------------------------------------------------
// Self-service
// ---------------------------------------------------------------------------

export function useMyLeaves() {
  return useQuery({
    queryKey: leavesKeys.my(),
    queryFn: () => api.get<LeaveListResponse>('/v1/my/leaves'),
  });
}

export function useMyLeaveBalance(year?: number) {
  return useQuery({
    queryKey: leavesKeys.myBalance(year),
    queryFn: () =>
      api.get<LeaveBalanceResponse>(
        `/v1/my/leaves/balance${year ? `?year=${year}` : ''}`,
      ),
  });
}

export function useApplyLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveApplyRequest) => api.post<LeaveResponse>('/v1/my/leaves', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leavesKeys.my() });
      qc.invalidateQueries({ queryKey: ['leaves', 'my', 'balance'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin — per-staff views + manual entry
// ---------------------------------------------------------------------------

export function useStaffLeaves(staffId: string | undefined) {
  return useQuery({
    queryKey: leavesKeys.staff(staffId ?? '__none__'),
    queryFn: () => api.get<LeaveListResponse>(`/v1/staff/${staffId}/leaves`),
    enabled: Boolean(staffId),
  });
}

export function useStaffLeaveBalance(staffId: string | undefined, year?: number) {
  return useQuery({
    queryKey: leavesKeys.staffBalance(staffId ?? '__none__', year),
    queryFn: () =>
      api.get<LeaveBalanceResponse>(
        `/v1/staff/${staffId}/leaves/balance${year ? `?year=${year}` : ''}`,
      ),
    enabled: Boolean(staffId),
  });
}

export function useManualLeaveEntry(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveManualEntryRequest) =>
      api.post<LeaveResponse>(`/v1/staff/${staffId}/leaves/manual`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leavesKeys.staff(staffId) });
      qc.invalidateQueries({ queryKey: ['leaves', 'staff', staffId, 'balance'] });
      qc.invalidateQueries({ queryKey: leavesKeys.queue() });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin — approval queue
// ---------------------------------------------------------------------------

/** `enabled` defaults true; pass `false` for any caller the endpoint would
 * 403 (staff/teacher — see backend `_ADMIN_ROLES`) so the request never
 * fires, matching the `useDataQualityOpenCount` precedent (11F.1). */
export function useLeaveQueue(status?: LeaveStatus | '', enabled = true) {
  return useQuery({
    queryKey: leavesKeys.queue(status || undefined),
    queryFn: () =>
      api.get<LeaveListResponse>(`/v1/leaves${status ? `?status=${status}` : ''}`),
    enabled,
  });
}

function useDecideLeave(action: 'approve' | 'deny') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeaveDecision }) =>
      api.post<LeaveResponse>(`/v1/leaves/${id}/${action}`, body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['leaves'] });
      qc.invalidateQueries({ queryKey: leavesKeys.staff(result.staff_id) });
    },
  });
}

export function useApproveLeave() {
  return useDecideLeave('approve');
}

export function useDenyLeave() {
  return useDecideLeave('deny');
}

// ---------------------------------------------------------------------------
// Config — tenant-wide annual_leave_allocation
// ---------------------------------------------------------------------------

export function useLeavesConfig() {
  return useQuery({
    queryKey: leavesKeys.config(),
    queryFn: () => api.get<LeavesConfig>('/v1/config/leaves'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateLeavesConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (annual_leave_allocation: number) =>
      api.patch<LeavesConfig>('/v1/config/leaves', { annual_leave_allocation }),
    onSuccess: (data) => {
      qc.setQueryData(leavesKeys.config(), data);
    },
  });
}
